/**
 * TrackedDomainCard — Sprint 2.6
 *
 * Card for a single tracked (competitor) domain on the Tracked tab.
 * Shows domain name, state, and a remove action.
 */
import { useState } from 'react';
import { api } from '../api';
import type { TrackedDomainSummary } from '../types';

interface Props {
  domain: TrackedDomainSummary;
  onRemoved: (id: string) => void;
}

const STATE_LABELS: Record<string, string> = {
  active:   'Active',
  inactive: 'Inactive',
  scanning: 'Scanning…',
  pending:  'Pending',
};

const STATE_COLOURS: Record<string, string> = {
  active:   'text-brand-green bg-green-50',
  inactive: 'text-amber-600 bg-amber-50',
  scanning: 'text-blue-600 bg-blue-50',
  pending:  'text-gray-500 bg-gray-100',
};

export function TrackedDomainCard({ domain, onRemoved }: Props) {
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState('');

  async function handleRemove() {
    if (!window.confirm(`Remove tracked domain "${domain.domain}"?`)) return;
    setRemoving(true);
    setError('');
    try {
      await api.domains.delete(domain.id);
      onRemoved(domain.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove');
      setRemoving(false);
    }
  }

  const stateLabel  = STATE_LABELS[domain.state]  ?? domain.state;
  const stateColour = STATE_COLOURS[domain.state] ?? 'text-gray-500 bg-gray-100';

  return (
    <div className="flex items-center gap-3 bg-white border border-gray-100 rounded-xl px-4 py-3">
      {/* Favicon fallback */}
      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0 text-xs font-bold text-gray-400 uppercase">
        {domain.domain.charAt(0)}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-brand-ink truncate">{domain.domain}</p>
        <p className="text-xs text-gray-400 mt-0.5">
          Tracked since {new Date(domain.createdAt).toLocaleDateString()}
        </p>
      </div>

      {/* State badge */}
      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full shrink-0 ${stateColour}`}>
        {stateLabel}
      </span>

      {/* Remove */}
      <button
        type="button"
        onClick={() => void handleRemove()}
        disabled={removing}
        className="text-gray-300 hover:text-red-400 transition-colors disabled:opacity-40 text-lg leading-none"
        title="Remove tracked domain"
      >
        ×
      </button>

      {error && <p className="text-xs text-red-500 w-full">{error}</p>}
    </div>
  );
}
